/**
 * CrmReferenceLibraryView — admin "References" tab in the CRM workspace (docs/REFERENCES.md).
 * Precedent: CrmFeeModelsView.jsx — a firm-wide, curated dataset reused across opportunities
 * (there: fee models feeding proposals; here: past-client references feeding reference letters).
 * Lives in CRM, not Templates — this is CRM content, not a structural template that gets
 * instantiated onto a project the way milestone/drawing-sheet/checklist templates do.
 *
 * A Reference is a past client who has given PERMISSION to be contacted by prospects — distinct
 * from a sales referral. Each entry links to an existing Contact (picked via ContactPicker.jsx,
 * never typed by hand — name/email/phone are resolved live from the Contact, never duplicated
 * here). Their project(s) are resolved live too (2026-08-19) — never typed in: a contact is a
 * "client" of a Project when they belong to that Project's own Client Organization, same
 * derivation services/team.js uses for the Team roster's Client bucket. A contact who's the client
 * on more than one project gets a checklist of candidate projects with a toggle per project
 * (opt-out — new projects default to included). Mirrors PunchRoomGuidanceEditor.jsx's
 * expand-in-place list pattern, flattened to one level.
 */
import { useState, useCallback, useEffect } from 'react'
import { F } from './CrmModal.jsx'
import ContactPicker from './ContactPicker.jsx'
import { confirmDialog } from '../Dialog.jsx'
import {
  getReferenceLibrary, createReferenceLibraryEntry, updateReferenceLibraryEntry,
  deleteReferenceLibraryEntry, moveReferenceLibraryEntry,
} from '../../services/referenceLibrary.js'

// ─── Toast system (mirrors TemplatesWorkspace.jsx's own, self-contained here rather than
// cross-importing between unrelated workspaces) ─────────────────────────────────────────────
function Toasts({ toasts }) {
  if (!toasts.length) return null
  return (
    <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 600, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', pointerEvents: 'none' }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          padding: '9px 18px', borderRadius: 6, fontSize: 13, fontFamily: 'DM Sans, sans-serif',
          background: t.type === 'error' ? '#d9534f' : '#07272D', color: '#fff',
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        }}>
          {t.msg}
        </div>
      ))}
    </div>
  )
}
function useToasts() {
  const [toasts, setToasts] = useState([])
  const addToast = useCallback((msg, type = 'success') => {
    const id = Date.now() + Math.random()
    setToasts(t => [...t, { id, msg, type }])
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500)
  }, [])
  return { toasts, addToast }
}

const LABEL = { fontSize: 10, fontFamily: 'Montserrat, sans-serif', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 3, display: 'block' }

/** Compact summary of the included projects for the row header — one project shows its type
 *  inline, more than one shows a count (the expanded checklist below has the full detail). */
function projectSummary(entry) {
  const n = (entry.projects || []).length
  if (n === 0) return ''
  if (n === 1) return entry.projects[0].projectType || entry.projects[0].location || '1 project'
  return n + ' projects'
}

function EntryRow({ entry, expanded, onToggle, onSave, onDelete, onMove, isFirst, isLast, addToast }) {
  async function toggleProject(projectId, included) {
    const excluded = new Set(entry.excludedProjectIds || [])
    if (included) excluded.delete(projectId); else excluded.add(projectId)
    try { await onSave({ excludedProjectIds: Array.from(excluded) }) }
    catch (e) { addToast('Save failed: ' + (e.message || 'unknown error'), 'error') }
  }

  const summary = projectSummary(entry)
  return (
    <div style={{ marginBottom: 6, opacity: entry.active ? 1 : 0.55 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 10px 8px', borderBottom: '0.5px solid var(--aj-border-mid)' }}>
        <button onClick={onToggle} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 11, padding: 0, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 500, fontSize: 12.5, fontFamily: 'DM Sans, sans-serif' }}>{entry.name}</span>
          {summary && <span style={{ fontSize: 11.5, color: 'var(--aj-text-mid)', marginLeft: 8 }}>{summary}</span>}
        </div>
        {!entry.active && <span style={{ fontSize: 10, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-muted)', textTransform: 'uppercase' }}>Inactive</span>}
        <button onClick={() => onMove('up')} disabled={isFirst} title="Move up"
          style={{ background: 'none', border: 'none', cursor: isFirst ? 'default' : 'pointer', color: isFirst ? 'var(--aj-border-mid)' : 'var(--aj-text-muted)', fontSize: 12, padding: '0 3px' }}>▲</button>
        <button onClick={() => onMove('down')} disabled={isLast} title="Move down"
          style={{ background: 'none', border: 'none', cursor: isLast ? 'default' : 'pointer', color: isLast ? 'var(--aj-border-mid)' : 'var(--aj-text-muted)', fontSize: 12, padding: '0 3px' }}>▼</button>
        <button onClick={onDelete} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 14, padding: '0 4px' }}>×</button>
      </div>
      {expanded && (
        <div style={{ padding: '10px 10px 14px 30px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <span style={LABEL}>Contact</span>
            <ContactPicker
              value={entry.contactId} name={entry.name}
              onChange={(id, name) => { if (id) onSave({ contactId: id }).catch(e => addToast('Save failed: ' + (e.message || 'unknown error'), 'error')) }}
            />
            <div style={{ fontSize: 11, color: 'var(--aj-text-mid)', marginTop: 4, fontFamily: 'DM Mono, monospace' }}>
              {[entry.phone, entry.email].filter(Boolean).join('  ·  ') || 'No phone/email on file for this contact.'}
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <span style={LABEL}>Client projects (auto-detected — toggle any off)</span>
            {(entry.candidateProjects || []).length === 0 ? (
              <div style={{ fontSize: 12, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text-mid)', fontStyle: 'italic' }}>
                No client projects found for this contact yet — once they're linked as the Client on a project, it'll show up here automatically.
              </div>
            ) : (
              <div>
                {entry.candidateProjects.map(p => {
                  const included = !(entry.excludedProjectIds || []).includes(p.id)
                  return (
                    <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12.5, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={included} onChange={e => toggleProject(p.id, e.target.checked)} />
                      <span style={{ flex: 1 }}>{p.name}</span>
                      <span style={{ color: 'var(--aj-text-mid)', fontSize: 11.5 }}>{[p.projectType, p.location].filter(Boolean).join('  —  ')}</span>
                    </label>
                  )
                })}
              </div>
            )}
          </div>
          <label style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text-mid)', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!entry.active} onChange={e => onSave({ active: e.target.checked }).catch(() => addToast('Update failed', 'error'))} />
            Active (shown in the picker when building a reference letter)
          </label>
        </div>
      )}
    </div>
  )
}

export default function CrmReferenceLibraryView() {
  const [entries, setEntries] = useState(null)   // null = loading
  const [expanded, setExpanded] = useState({})
  const [newContactId, setNewContactId] = useState('')
  const [newContactName, setNewContactName] = useState('')
  const [creating, setCreating] = useState(false)
  const { toasts, addToast } = useToasts()

  function load() { getReferenceLibrary().then(setEntries).catch(() => setEntries([])) }
  useEffect(load, [])

  async function handleCreate() {
    if (!newContactId || creating) return
    setCreating(true)
    try {
      const e = await createReferenceLibraryEntry({ contactId: newContactId })
      setEntries(prev => [...(prev || []), e])
      setExpanded(x => ({ ...x, [e.id]: true }))
      setNewContactId(''); setNewContactName('')
      addToast('Reference added')
    } catch (e) { addToast('Create failed: ' + (e.message || 'unknown error'), 'error') } finally { setCreating(false) }
  }

  function patchEntry(id, patch) { setEntries(prev => (prev || []).map(e => e.id === id ? { ...e, ...patch } : e)) }

  async function handleDelete(e) {
    if (!(await confirmDialog({ title: 'Delete reference?', message: `Delete "${e.name}" from the reference library? This cannot be undone.`, confirmLabel: 'Delete', danger: true }))) return
    try {
      await deleteReferenceLibraryEntry(e.id)
      setEntries(prev => (prev || []).filter(x => x.id !== e.id))
      addToast('Reference deleted')
    } catch (err) { addToast('Delete failed: ' + (err.message || 'unknown error'), 'error') }
  }

  async function handleMove(e, direction) {
    const list = entries || []
    const idx = list.findIndex(x => x.id === e.id)
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    if (swapIdx < 0 || swapIdx >= list.length) return
    const next = [...list]
    ;[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]]
    setEntries(next)
    try { await moveReferenceLibraryEntry(e.id, direction) } catch (_) { addToast('Reorder failed', 'error') }
  }

  // "change" on a saved entry's ContactPicker updates the contact link, then the resolved
  // name/email/phone need to come back from the server — simplest is a full reload of that row.
  async function saveEntry(id, patch) {
    const updated = await updateReferenceLibraryEntry(id, patch)
    patchEntry(id, updated)
  }

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <div>
          <div style={S.title}>References</div>
          <div style={S.sub}>Past clients who've given permission to be contacted by prospects. Each Opportunity's References section picks an ordered subset of this list for its own letter — curate it here once.</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 14 }}>
        <div style={{ width: 280 }}>
          <ContactPicker value={newContactId} name={newContactName} onChange={(id, name) => { setNewContactId(id); setNewContactName(name) }} />
        </div>
        <button onClick={handleCreate} disabled={creating || !newContactId} style={F.btnPrimary}>{creating ? 'Adding…' : '+ Add Reference'}</button>
      </div>

      <div style={S.list}>
        {entries === null && (
          <div style={S.muted}>Loading reference library…</div>
        )}
        {Array.isArray(entries) && entries.length === 0 && (
          <div style={S.muted}>No references yet — search for a past client above; their client project(s) are detected automatically.</div>
        )}
        {(entries || []).map((e, i) => (
          <EntryRow
            key={e.id} entry={e}
            expanded={!!expanded[e.id]}
            onToggle={() => setExpanded(x => ({ ...x, [e.id]: !x[e.id] }))}
            onSave={patch => saveEntry(e.id, patch)}
            onDelete={() => handleDelete(e)}
            onMove={dir => handleMove(e, dir)}
            isFirst={i === 0} isLast={i === (entries || []).length - 1}
            addToast={addToast}
          />
        ))}
      </div>
      <Toasts toasts={toasts} />
    </div>
  )
}

const S = {
  wrap: { maxWidth: 820, margin: '0 auto', padding: '18px 20px 60px' },
  head: { marginBottom: 14 },
  title: { fontFamily: '"Montserrat", sans-serif', fontSize: 14, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  sub: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', marginTop: 4, maxWidth: 620, lineHeight: 1.5 },
  list: { display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)', overflow: 'hidden', padding: '2px 8px' },
  muted: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)', fontStyle: 'italic', padding: '20px 14px', textAlign: 'center' },
}
